import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, LanguageContextType } from '../types/language';
import { languageApi } from '../services/api';

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<Language | null>(null);
  const [availableLanguages, setAvailableLanguages] = useState<Language[]>([]);
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);

  const loadLanguages = async () => {
    try {
      const languages = await languageApi.getAll();
      setAvailableLanguages(languages);
      
      const savedLanguage = localStorage.getItem('selectedLanguage');
      const defaultLanguage = languages.find(lang => lang.is_default) || languages[0];
      const selectedLanguage = savedLanguage 
        ? languages.find(lang => lang.code === savedLanguage) || defaultLanguage
        : defaultLanguage;

      if (selectedLanguage) {
        await loadTranslations(selectedLanguage.code);
        setCurrentLanguage(selectedLanguage);
      }
    } catch (error) {
      console.error('Error loading languages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTranslations = async (languageCode: string) => {
    try {
      const response = await languageApi.getTranslationsByLanguage(languageCode);
      setTranslations(response.translations);
    } catch (error) {
      console.error('Error loading translations:', error);
      setTranslations({});
    }
  };

  const changeLanguage = async (languageCode: string) => {
    setIsLoading(true);
    try {
      const language = availableLanguages.find(lang => lang.code === languageCode);
      if (language) {
        await loadTranslations(languageCode);
        setCurrentLanguage(language);
        localStorage.setItem('selectedLanguage', languageCode);
      }
    } catch (error) {
      console.error('Error changing language:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const t = (key: string, group?: string): string => {
    const translationKey = group ? `${group}.${key}` : key;
    return translations[translationKey] || translations[key] || key;
  };

  const refreshTranslations = async () => {
    if (currentLanguage) {
      await loadTranslations(currentLanguage.code);
    }
  };

  useEffect(() => {
    loadLanguages();
  }, []);

  const value: LanguageContextType = {
    currentLanguage,
    availableLanguages,
    translations,
    changeLanguage,
    t,
    isLoading,
    refreshTranslations,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;